import React, { useEffect, useState } from 'react';
import { Col, FormInput, FormRadioGroup, Row } from '@snsw/react-component-library';
import PropTypes from 'prop-types';
import { isEmpty, isValidEmail, validateExt } from 'shared/utils/Validation';
import { NewContactNumber } from 'shared/components/contactNumber/NewContactNumber';
import { PhoneNumber } from 'shared/utils/PhoneNumber';
import { PlantItemNewOngoingContactTypes as OngoingContactTypes } from 'plantItemNew/constants/plantItemConstants';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';

export const IndividualOngoingContactFields = React.forwardRef((props, ref) => {
  const { value: savedOngoingContact = {}, onErrorChange = () => {}, amIRegistrationHolder } = props;
  const {
    contactType = '',
    emailAddress: savedEmailAddress = '',
    phoneNumber: savedPhoneNumber = {},
  } = savedOngoingContact;

  const ongoingContactTypeOptions = [
    { label: OngoingContactTypes.me.label, value: OngoingContactTypes.me.value },
    { label: OngoingContactTypes.someoneElse.label, value: OngoingContactTypes.someoneElse.value },
  ];

  if (!amIRegistrationHolder) {
    let option = {
      label: OngoingContactTypes.registrationHolder.label,
      value: OngoingContactTypes.registrationHolder.value,
    };
    ongoingContactTypeOptions.splice(1, 0, option);
  }

  const [contactTypeValue, setContactTypeValue] = useState(contactType);
  const [emailAddress, setEmailAddress] = useState(savedEmailAddress);
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber);
  const [requirePhoneNumber, setRequirePhoneNumber] = useState(false);
  const [requireEmailAddress, setRequireEmailAddress] = useState(false);

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);

  useEffect(() => {
    if (contactType === OngoingContactTypes.someoneElse.value) {
      setRequireEmailAddress(true);
      setRequirePhoneNumber(true);
    } else if (amIRegistrationHolder && contactType === OngoingContactTypes.me.value) {
      setRequireEmailAddress(false);
      setRequirePhoneNumber(false);
    } else if (!amIRegistrationHolder && contactType === OngoingContactTypes.me.value) {
      setRequireEmailAddress(false);
      setRequirePhoneNumber(true);
    } else if (!amIRegistrationHolder && contactType === OngoingContactTypes.registrationHolder.value) {
      setRequireEmailAddress(false);
      setRequirePhoneNumber(false);
    }
  }, [amIRegistrationHolder, contactType]);

  useEffect(() => {
    if (!contactTypeValue) return;

    let ongoingContact = {
      contactType: contactTypeValue,
      emailAddress: requireEmailAddress ? emailAddress : '',
      phoneNumber: requirePhoneNumber ? phoneNumber : null,
    };

    props.onChange(ongoingContact);

    if (showErrors) validateAll();
  }, [contactTypeValue, emailAddress, phoneNumber, requireEmailAddress, requirePhoneNumber]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const validators = [
    {
      fieldName: 'contactTypeValue',
      fieldValue: contactTypeValue,
      isValid: (v) => ongoingContactTypeOptions.map((opt) => opt.value).includes(v),
      errorMessage: () => `${ItemRegistrationHolderPage.ongoingContactTypeLabel} is required.`,
    },
    {
      fieldName: 'ongoingEmailAddress',
      fieldValue: emailAddress,
      isValid: (v) => !requireEmailAddress || isValidEmail(v),
      errorMessage: (v) => (isEmpty(v) ? 'Email address is required.' : 'Enter a valid email address.'),
    },
    {
      fieldName: 'ongoingPhoneNumber',
      fieldValue: phoneNumber,
      isValid: (v) => !requirePhoneNumber || !!v?.formattedNumber,
      errorMessage: (v) => (isEmpty(v?.raw?.number) ? 'Phone number is required.' : 'Enter a valid phone number.'),
    },
  ];

  const validateAll = () => {
    let isValid = validateExt(validators, setErrors, setErrorSummary);
    setShowErrors(!isValid);
    return isValid;
  };

  if (ref) {
    ref.current = {
      validate: () => validateAll(),
    };
  }

  return (
    <>
      <FormRadioGroup
        id="contactTypeValue"
        name="contactTypeValue"
        legend={ItemRegistrationHolderPage.ongoingContactTypeLabel}
        helpMessage="We'll send renewal notices and important information to this person"
        vertical={true}
        options={ongoingContactTypeOptions}
        onChange={setContactTypeValue}
        value={contactTypeValue}
        errorMessage={errors?.contactTypeValue?.errorMessage}
        hasError={showErrors && errors?.contactTypeValue?.invalid}
      />

      {requireEmailAddress && (
        <Row style={{ marginTop: 20 }}>
          <Col span={6}>
            <FormInput
              id="ongoingEmailAddress"
              label={ItemRegistrationHolderPage.emailAddressLabel}
              hasError={showErrors && errors?.ongoingEmailAddress?.invalid}
              errorMessage={errors?.ongoingEmailAddress?.errorMessage}
              onChange={(e, { value }) => setEmailAddress(value)}
              value={emailAddress}
            />
          </Col>
        </Row>
      )}

      {requirePhoneNumber && (
        <Row style={{ marginTop: 20 }}>
          <Col span={12}>
            <NewContactNumber
              id="ongoingPhoneNumber"
              label={ItemRegistrationHolderPage.ongoingContactPhoneNumberLabel}
              helpMessage={ItemRegistrationHolderPage.ongoingContactPhoneNumberHelpText}
              onChange={(v) => setPhoneNumber(new PhoneNumber('', v, undefined, true))}
              value={phoneNumber?.raw?.number}
              hasError={showErrors && errors?.ongoingPhoneNumber?.invalid}
              errorMessage={errors?.ongoingPhoneNumber?.errorMessage}
            />
          </Col>
        </Row>
      )}
    </>
  );
});

IndividualOngoingContactFields.displayName = 'IndividualOngoingContactFields';

IndividualOngoingContactFields.propTypes = {
  onChange: PropTypes.func,
  amIRegistrationHolder: PropTypes.bool,
};

IndividualOngoingContactFields.defaultProps = {
  onChange: null,
};
